/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Typography, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { EventIcon } from 'components/elements/Icons';
import { InputLabel, Select, Textarea, StaticDatePicker, ButtonGroup } from 'components/elements';
import { useDeviceDetect, useFetchLocations, useFetchTimeslots, useProfile, useFetchAppointments } from 'hooks';
import { periods } from 'const';
import { dateUtils } from 'utils';
import ProgressSubtitle from './ProgressSubtitle';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function BookAppointmentForm({ onSubmit, providerAccountId, providerName, healthcareServiceId, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { isDesktop } = useDeviceDetect();
  const { data: profile } = useProfile();
  const { data: locations } = useFetchLocations(providerAccountId, healthcareServiceId);
  const { data: appointments } = useFetchAppointments({ period: periods.history, healthcareServiceId, providerAccountId });

  const { handleSubmit, control, watch, resetField, setValue, setError, clearErrors } = useForm({
    mode: 'onSubmit',
    defaultValues: { timezone: profile?.timezone || null, locationId: '', ...restProps },
  });

  const locationId = watch('locationId');
  const timezone = watch('timezone');
  const date = watch('date');
  const timeSlot = watch('timeSlot');
  const firstDay = date ? date.startOf('month') : DateTime.now().startOf('month');

  const [month, setMonth] = useState(firstDay);
  const [isDateSelected, setIsDateSelected] = useState(false);
  const isMobileDateSelectedView = !!date && isDateSelected;
  const selectedDateString = dateUtils.formatDateTimeString(date);
  const { data: allTimeslots, availableDays, isLoading } = useFetchTimeslots(providerAccountId, healthcareServiceId, locationId, month, timezone);

  const timeslots = useMemo(() => {
    if (date) {
      return allTimeslots
        .filter((slot) => {
          const slotDate = DateTime.fromISO(slot.value).setZone('UTC');
          return slotDate.ordinal === date.ordinal;
        })
        .sort((slot) => new Date(slot.value));
    }
    return [];
  }, [allTimeslots, date]);

  useEffect(() => {
    resetField('timeSlot');
    setValue('timeSlot', null);
    setError('timeSlot', { type: 'custom', message: t('please_fill_in_this_field') });
  }, [date]);

  useEffect(() => {
    if (timeSlot) {
      clearErrors('timeSlot');
    } else {
      setError('timeSlot', { type: 'custom', message: t('please_fill_in_this_field') });
    }
  }, [timeSlot]);

  useEffect(() => {
    if (!locationId) {
      if (locations.length > 0 && appointments.length > 0) {
        const prevAppointment = appointments
          .sort((a, b) => new Date(a.dateOfService) > new Date(b.dateOfService))
          .find(({ attributes }) => attributes.providerAccountId === providerAccountId && attributes.healthcareServiceId === healthcareServiceId);
        const previousAppointmentLocationId = locations.find(({ value }) => prevAppointment?.attributes.locationId === value)?.value;
        if (previousAppointmentLocationId) {
          setValue('locationId', previousAppointmentLocationId);
        }
      }
    }
  }, [locations, appointments]);

  // eslint-disable-next-line react/no-unstable-nested-components
  function TimeSlots() {
    return (
      <div className={classes.timeSlotSelector}>
        <Controller
          name="timeSlot"
          control={control}
          rules={{
            required: {
              value: true,
              message: t('please_fill_in_this_field'),
            },
          }}
          render={({ field: { ref, ...restField }, fieldState: { error } }) => (
            <ButtonGroup
              isLoading={isLoading}
              options={timeslots}
              error={!!error}
              helperText={error ? error.message : null}
              {...restField}
              required
              label={<InputLabel text={t('select_date')} required />}
            />
          )}
        />
      </div>
    );
  }

  const formStyles = cn(classes.bookAppointmentForm);

  return (
    <form
      className={formStyles}
      onSubmit={handleSubmit((data, ...rest) => {
        const location = locations.find((x) => x.value === data.locationId);
        const slot = timeslots.find((x) => x.value === data.timeSlot);
        onSubmit({ ...data, location, slot: slot?.data }, ...rest);
      })}
    >
      <ProgressSubtitle>
        <Typography variant="h6" component="span">
          <>
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {t('follow_up_appointment')}
            </Typography>{' '}
            <Typography variant="h6" component="span">
              {t('with')}
            </Typography>{' '}
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {providerName}
            </Typography>
          </>
        </Typography>
      </ProgressSubtitle>

      <div className={classes.selectorsWrapper}>
        <div className={classes.selector}>
          <Controller
            name="locationId"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('please_fill_in_this_field'),
              },
            }}
            render={({ field: { ref, ...restField }, fieldState: { error } }) => (
              <Select options={locations} label={<InputLabel text={t('location')} required />} helperText={error ? error.message : null} error={!!error} {...restField} />
            )}
          />
        </div>
        <div className={classes.selector}>
          <Typography sx={{ textAlign: 'center' }} variant="body2" component="p">
            {t('timezone')}
          </Typography>{' '}
          <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }} variant="body1" component="p">
            {timezone}
          </Typography>
        </div>
      </div>
      <div className={cn(classes.selectorsWrapper, { [classes.hiddenSelectors]: !timezone?.length > 0 || !locationId })}>
        {(isDesktop || !isMobileDateSelectedView) && (
          <div className={classes.selector}>
            <Controller
              name="date"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('please_fill_in_this_field'),
                },
                validate: (value) => {
                  if (!value) return t('invalid_date');
                  return null;
                },
              }}
              render={({ field: { ref, onChange, ...restField }, fieldState: { error } }) => (
                <StaticDatePicker
                  availableDays={availableDays}
                  error={!!error}
                  helperText={error ? error.message : null}
                  {...restField}
                  label={<InputLabel text={t('date')} required />}
                  onMonthChange={setMonth}
                  onViewChange={() => setIsDateSelected(false)}
                  onChange={(e) => {
                    onChange(e);
                    setIsDateSelected(true);
                  }}
                />
              )}
            />
          </div>
        )}
        {isDesktop ? (
          <TimeSlots timezone={timezone} />
        ) : (
          isDateSelected && (
            <div className={classes.mobileSelectedDate}>
              <button className={classes.calendarButton} type="button" onClick={() => setIsDateSelected(false)}>
                <EventIcon classes={classes.eventIcon} />
              </button>
              <Typography variant="body2" component="div" noWrap>
                {selectedDateString}
              </Typography>
              <TimeSlots />
            </div>
          )
        )}
      </div>

      <Controller
        name="reasonForVisit"
        control={control}
        render={({ field: { ref, ...restField } }) => <Textarea label={<InputLabel text={t('reason_for_visit')} />} hasLimitCounter={false} {...restField} />}
      />
      <Button type="submit" variant="contained" color="primary" size="large" className={classes.submitButton} disabled={!timeSlot}>
        {t('next')}
      </Button>
    </form>
  );
}

BookAppointmentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  providerAccountId: PropTypes.string,
  providerName: PropTypes.string,
  healthcareServiceId: PropTypes.string,
  date: PropTypes.instanceOf(DateTime),
};

BookAppointmentForm.defaultProps = {
  date: DateTime.now().startOf('day'),
  providerAccountId: '',
  providerName: '',
  healthcareServiceId: '',
};

export default BookAppointmentForm;
